import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { Separator } from '../Separator';
import { MetricCard } from './MetricCard';
import { ExecutiveSummary } from './ExecutiveSummary';
import { EngagementMetrics } from './EngagementMetrics';
import { TrafficVolumeChart } from './TrafficVolumeChart';
import { ReferrerChart } from './ReferrerChart';
import { ShareBreakdown } from './ShareBreakdown';
import { TopPathsTable } from './TopPathsTable';
import { SectionSplitChart } from './SectionSplitChart';
import { RetryDistributionChart } from './RetryDistributionChart';
import { TechnicalBreakdowns } from './TechnicalBreakdowns';
import { GeographyBreakdowns } from './GeographyBreakdowns';
import { NetworkIntegrityCard } from './NetworkIntegrityCard';
import { LocalHourChart } from './LocalHourChart';
import { OriginInferenceCard } from './OriginInferenceCard';
import { RestrictedJurisdictionsCard } from './RestrictedJurisdictionsCard';
import { AccessMethodsCard } from './AccessMethodsCard';
import { LocaleSignalsCard } from './LocaleSignalsCard';
import type { TrafficPayload } from '../../utils/traffic';
interface TrafficSectionsProps {
  payload: TrafficPayload;
}
export function TrafficSections({
  payload
}: TrafficSectionsProps) {
  const {
    summary,
    series,
    regions,
    countries,
    cities,
    paths,
    sections,
    devices,
    referrers,
    retryDistribution,
    browsers,
    languages,
    connections,
    mediums,
    coverage,
    vpnNetworks,
    vpnRoutes,
    localHours,
    originInference,
    likelyOrigins,
    originCoverage,
    accessMethods,
    restrictedAccess,
    restrictedHits,
    restrictedListSize,
    localeSignals
  } = payload;
  return <>
      <div className="mt-4 sm:mt-6" data-id="element-499" data-mp="psf23nlkm2dk">
        <ExecutiveSummary summary={summary} sections={sections} data-id="element-500" data-mp="23vu796fnrm0" />
      </div>

      <section aria-label="Operational metrics" className="mt-4 grid grid-cols-2 gap-3 sm:mt-6 sm:gap-4 lg:grid-cols-4" data-id="element-501" data-mp="21sv7znayo55">
        <MetricCard label="Requests" value={summary.totalRequests.toLocaleString()} hint="Total hits on the maintenance page this window" data-id="element-502" data-mp="1x83maf7oi0v2" />
        <MetricCard label="Unique visitors" value={summary.uniqueVisitors.toLocaleString()} hint="Distinct sessions reaching the page" data-id="element-503" data-mp="26xdhw51qe1nx" />
        <MetricCard label="Retry rate" value={`${Math.round(summary.repeatRetryRate * 100)}%`} hint="Visitors who hit “Check again” at least once" data-id="element-504" data-mp="2b0jjgzdwf4o2" />
        <MetricCard label="Peak" value={`${summary.peakRps} rps`} hint="Highest sustained request rate this window" data-id="element-505" data-mp="1j9n5fvg3qtz7" />
      </section>

      <div className="mt-4 sm:mt-6" data-id="element-506" data-mp="1s90i0wzivyo5">
        <EngagementMetrics summary={summary} data-id="element-507" data-mp="1s5urref5qqqu" />
      </div>

      <section className="mt-4 sm:mt-6" data-id="element-508" data-mp="2ynhv9jr2pofn">
        <Card data-id="element-509" data-mp="2yqnjyxvsioau">
          <CardHeader data-id="element-510" data-mp="33n48ht6wj4og">
            <CardTitle data-id="element-511" data-mp="33ml299qm64q9">Traffic pattern over the window</CardTitle>
            <CardDescription data-id="element-512" data-mp="3drb7bohbdoyp">
              Requests, unique visitors, and retries across the maintenance window · switch the axis
              between UTC and your own time zone; tooltips show both
            </CardDescription>
          </CardHeader>
          <CardContent data-id="element-513" data-mp="3406scorn243v">
            <TrafficVolumeChart series={series} data-id="element-514" data-mp="33zexxh9s2cf6" />
          </CardContent>
        </Card>
      </section>

      <div className="mt-4 sm:mt-6" data-id="element-515" data-mp="2zxtoy70d6ia9">
        <GeographyBreakdowns countries={countries} cities={cities} regions={regions} data-id="element-516" data-mp="2v1crunb6stl1" />
      </div>

      <section className="mt-4 sm:mt-6" data-id="element-517" data-mp="2q174vkfv3v9a">
        <Card data-id="element-518" data-mp="2q05kuqgvbyff">
          <CardHeader data-id="element-519" data-mp="2q3td7makuesk">
            <CardTitle data-id="element-520" data-mp="2l2dqzqwny28l">Demand by visitor local time</CardTitle>
            <CardDescription data-id="element-521" data-mp="2l2x8j6ieiom3">
              The same traffic plotted against each visitor&rsquo;s own clock instead of UTC — a
              spike at 9am local is the start of someone&rsquo;s working day, wherever they are
            </CardDescription>
          </CardHeader>
          <CardContent data-id="element-522" data-mp="fb09lmr5pt84">
            <LocalHourChart points={localHours} data-id="element-523" data-mp="fago5w2asmpc" />
          </CardContent>
        </Card>
      </section>

      <section className="mt-4 sm:mt-6" data-id="element-524" data-mp="maspn39nk091">
        <Card data-id="element-525" data-mp="rci12k0sefxn">
          <CardHeader data-id="element-526" data-mp="rax7awmar10u">
            <CardTitle data-id="element-527" data-mp="rbggnoxvsh39">Where people were headed, by area</CardTitle>
            <CardDescription data-id="element-528" data-mp="dcysxr7z74lc">
              Requested URLs rolled up to their top-level area — dark bars are requests, light bars
              are distinct visitors
            </CardDescription>
          </CardHeader>
          <CardContent data-id="element-529" data-mp="3lm478hh4ujj">
            <SectionSplitChart sections={sections} data-id="element-530" data-mp="3kaz95calgqu" />
          </CardContent>
        </Card>
      </section>

      <section className="mt-4 grid gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-3" data-id="element-531" data-mp="8f74j76ji0lw">
        <Card className="lg:col-span-2" data-id="element-532" data-mp="3pv6wl2d0e1zh">
          <CardHeader data-id="element-533" data-mp="3usowy4cyn8vv">
            <CardTitle data-id="element-534" data-mp="3urxapk596ni9">Most requested URLs</CardTitle>
            <CardDescription data-id="element-535" data-mp="3uqm3198osvlv">
              The exact pages people tried to reach, with distinct visitors per URL
            </CardDescription>
          </CardHeader>
          <CardContent data-id="element-536" data-mp="3lqpdl5eh3spr">
            <TopPathsTable paths={paths} data-id="element-537" data-mp="3lu3esabehvvm" />
          </CardContent>
        </Card>

        <Card data-id="element-538" data-mp="326qrdvo7vhz8">
          <CardHeader data-id="element-539" data-mp="328bb0q8ztgib">
            <CardTitle data-id="element-540" data-mp="2x744svmi0pdr">Devices</CardTitle>
            <CardDescription data-id="element-541" data-mp="2x85o1ndo4i6k">Session share by device class</CardDescription>
          </CardHeader>
          <CardContent data-id="element-542" data-mp="3b7yb98pzkg9s">
            <ShareBreakdown rows={devices.map(device => ({
            label: device.device,
            share: device.share
          }))} data-id="element-543" data-mp="3b88557e5j7kr" />
            <Separator className="my-6" data-id="element-544" data-mp="3l52zrf7b0ntr" />
            <dl className="space-y-3" data-id="element-545" data-mp="3l3rzvqgzc07x">
              <div className="flex items-baseline justify-between gap-4" data-id="element-546" data-mp="3l2qaegz7vb6o">
                <dt className="text-sm text-muted-foreground" data-id="element-547" data-mp="3g5gmj5rh77ky">Median time on page</dt>
                <dd className="font-mono text-sm tabular-nums text-foreground" data-id="element-548" data-mp="3utl05vbo8swv">
                  {summary.medianTimeOnPage}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4" data-id="element-549" data-mp="7pn8cxkma872">
                <dt className="text-sm text-muted-foreground" data-id="element-550" data-mp="7nty78asp5ub">Support email clicks</dt>
                <dd className="font-mono text-sm tabular-nums text-foreground" data-id="element-551" data-mp="2p0n1mizdqxb">
                  {summary.supportEmailClicks.toLocaleString()}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </section>

      <section className="mt-4 grid gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-2" data-id="element-552" data-mp="35jhzmlgdp42y">
        <Card data-id="element-553" data-mp="3aa847wgzd2wl">
          <CardHeader data-id="element-554" data-mp="3aaq556el85au">
            <CardTitle data-id="element-555" data-mp="3ab9n2n48mtov">How people arrived</CardTitle>
            <CardDescription data-id="element-556" data-mp="3f9tc4we4ollg">Referrer breakdown across the window</CardDescription>
          </CardHeader>
          <CardContent data-id="element-557" data-mp="3k8e5qcyvk70x">
            <ReferrerChart referrers={referrers} data-id="element-558" data-mp="3k7l57tqiedr8" />
          </CardContent>
        </Card>

        <Card data-id="element-559" data-mp="3p7h9zv71697u">
          <CardHeader data-id="element-560" data-mp="3p924jktchs5p">
            <CardTitle data-id="element-561" data-mp="3p8ihxqsr2flc">Retry pressure per visitor</CardTitle>
            <CardDescription data-id="element-562" data-mp="3t9lxec6vy9m1">
              How many times each person pressed “Check again” — the tail is the impatience signal
            </CardDescription>
          </CardHeader>
          <CardContent data-id="element-563" data-mp="1yurrep8gsv3a">
            <RetryDistributionChart buckets={retryDistribution} data-id="element-564" data-mp="1ywclkdqvac0m" />
          </CardContent>
        </Card>
      </section>

      <div className="mt-4 sm:mt-6" data-id="element-565" data-mp="1rdd3cbf56qiu">
        <TechnicalBreakdowns browsers={browsers} languages={languages} connections={connections} mediums={mediums} coverage={coverage} data-id="element-566" data-mp="1ml25rf7vjgtg" />
      </div>

      <div className="mt-4 sm:mt-6" data-id="element-567" data-mp="1dknjacj6bvod">
        <NetworkIntegrityCard summary={summary} networks={vpnNetworks} routes={vpnRoutes} data-id="element-568" data-mp="1dlosebgmq83g" />
      </div>

      <div className="mt-4 sm:mt-6" data-id="element-569" data-mp="2a9cwhpjuw040">
        <OriginInferenceCard rows={originInference} origins={likelyOrigins} coverage={originCoverage} data-id="element-570" data-mp="2a8a9hspnmhtd" />
      </div>

      <div className="mt-4 sm:mt-6" data-id="element-571" data-mp="218n651dd71xo">
        <AccessMethodsCard methods={accessMethods} data-id="element-572" data-mp="21bsx6m5k0dw1" />
      </div>

      <div className="mt-4 sm:mt-6" data-id="element-573" data-mp="1wa3jszuuzo4s">
        <LocaleSignalsCard signals={localeSignals} uniqueVisitors={summary.uniqueVisitors} data-id="element-574" data-mp="1wd7t2bn6la2b" />
      </div>

      <div className="mt-4 sm:mt-6" data-id="element-575" data-mp="kvpyo2rhpmsg">
        <RestrictedJurisdictionsCard hits={restrictedHits} listSize={restrictedListSize} coverage={originCoverage} uniqueVisitors={summary.uniqueVisitors} restrictedAccess={restrictedAccess} data-id="element-576" data-mp="kuonzzedz2b9" />
      </div>
    </>;
}